import { formatMoney, formatNumber, pluralize, toArray } from '@lib/utils';
import { generateFAQSchema } from '@lib/generate-schemas';
import { LISTING_STATUSES, CURRENT_LISTING_STATUSES } from '@lib/listing-statuses';

// ---------------------------------------------------------
// Pure, testable builders that generate AEO Q&A content and market summaries
// from data already available at render time. Each builder omits any item whose
// source data is missing so the page never shows broken placeholders or invented
// values. Answers are plain strings so they can feed both the visible FaqBlock and
// the FAQPage JSON-LD (see generateFAQSchema).
// ---------------------------------------------------------

// Keep only Q&A items that have both a question and a non-empty answer.
const compact = (items) =>
  items.filter((item) => item && item.question && item.answer && String(item.answer).trim());

/**
 * Resolves the Q&A items to render for a page that supports both a CMS-authored
 * `faqSection` and a generated fallback — authored content takes precedence when
 * present — and the matching FAQPage JSON-LD for those SAME items. Building both
 * from one array here (rather than at each call site) guarantees the visible
 * FaqBlock and the schema can never drift apart.
 * @param {object} authoredSection - a `faqSection` entry (or undefined/null)
 * @param {Array<{question: string, answer: string}>} generatedItems - fallback items
 * @returns {{ items: Array, schema: string|null }}
 */
export const resolveFaqContent = (authoredSection, generatedItems = []) => {
  const authored = buildFaqSectionItems(authoredSection);
  const items = authored.length > 0 ? authored : generatedItems;
  return { items, schema: generateFAQSchema(items) };
};

/**
 * Transforms a `faqSection` content block into { question, answer } pairs. Each
 * question1..question5 list holds the question first, then the answer across the
 * remaining items — Contentful short-text entries cap at 256 chars, so a long
 * answer is continued in further entries.
 * @param {object} section - a FaqSection entry
 * @returns {Array<{question: string, answer: string}>}
 */
export const buildFaqSectionItems = (section) => {
  if (!section) return [];

  const items = [];
  for (let i = 1; i <= 5; i += 1) {
    const pair = section[`question${i}`];
    if (Array.isArray(pair)) {
      const [question, ...answerParts] = pair;
      const answer = answerParts
        .filter((part) => typeof part === 'string' && part.trim())
        .join(' ')
        .trim();
      if (question && answer) items.push({ question, answer });
    }
  }

  return compact(items);
};

/**
 * Finds an embedded `faqSection` block within a Contentful rich-text body and
 * returns its Q&A items (used to build page-level FAQPage schema for content that
 * renders the block inline). Returns [] when no block is present.
 * @param {object} body - a rich-text field ({ json, links })
 * @returns {Array<{question: string, answer: string}>}
 */
export const extractFaqSectionItemsFromBody = (body) => {
  const blocks = body?.links?.entries?.block;
  if (!Array.isArray(blocks)) return [];
  const section = blocks.find((block) => block?.__typename === 'FaqSection');
  return buildFaqSectionItems(section);
};

// First non-empty paragraph of a multi-line text field.
const firstParagraph = (text) =>
  String(text || '')
    .split('\n')
    .map((p) => p.trim())
    .filter(Boolean)[0] || '';

const plural = (count, noun) => `${count} ${pluralize(noun, count)}`;

// Joins a list into prose with a serial comma: ["a"] -> "a", ["a","b"] -> "a and
// b", ["a","b","c"] -> "a, b, and c".
const joinWithAnd = (list) => {
  if (list.length <= 1) return list[0] || '';
  if (list.length === 2) return `${list[0]} and ${list[1]}`;
  return `${list.slice(0, -1).join(', ')}, and ${list[list.length - 1]}`;
};

/**
 * Builds ~5 property-specific Q&A pairs for a listing detail page.
 * @param {object} listing - the listing data object (LISTING_ATTRIBUTES fields)
 * @returns {Array<{question: string, answer: string}>}
 */
export const buildListingFaqItems = (listing = {}) => {
  const {
    address,
    city,
    state,
    price,
    priceFormatted,
    bedroomCount,
    bathroomCount,
    fullBathroomCount,
    partBathroomCount,
    propertyTypeDisplay,
    squareFootage,
    acres,
    status,
    schoolDistrict,
    openHouses
  } = listing;

  const label = address || [city, state].filter(Boolean).join(', ') || 'This property';
  const priceText = priceFormatted || (price ? formatMoney(price) : null);

  const items = [];

  if (priceText) {
    const locationSuffix = [city, state].filter(Boolean).join(', ');
    items.push({
      question: `How much does ${label} cost?`,
      answer: `${label}${locationSuffix ? `, ${locationSuffix}` : ''} is listed at ${priceText}.`
    });
  }

  if (bedroomCount || fullBathroomCount || bathroomCount) {
    // Keep each part atomic so joinWithAnd punctuates the list once; a pre-joined
    // phrase here yields "3 bedrooms and 2 full and 1 half bathrooms".
    const parts = [];
    if (bedroomCount) parts.push(plural(bedroomCount, 'bedroom'));

    if (fullBathroomCount) {
      // bathroomCount is the TOTAL, so it can't be labeled "full".
      parts.push(plural(fullBathroomCount, 'full bathroom'));
      if (partBathroomCount) parts.push(plural(partBathroomCount, 'half bathroom'));
    } else if (bathroomCount) {
      parts.push(plural(bathroomCount, 'bathroom'));
    }

    items.push({
      question: `How many bedrooms and bathrooms does ${label} have?`,
      answer: `${label} has ${joinWithAnd(parts)}.`
    });
  }

  if (propertyTypeDisplay) {
    const detailParts = [];
    if (squareFootage) detailParts.push(`approximately ${formatNumber(squareFootage)} square feet`);
    if (acres) detailParts.push(`${acres} acres`);
    const detailText = detailParts.length ? ` with ${detailParts.join(' on ')}` : '';
    items.push({
      question: `What type of property is ${label}?`,
      answer: `${label} is a ${propertyTypeDisplay}${detailText}.`
    });
  }

  if (status) {
    items.push({
      question: `Is ${label} still available?`,
      answer: `${label} is currently listed as ${status}.`
    });
  }

  if (schoolDistrict) {
    items.push({
      question: `What school district serves ${label}?`,
      answer: `${label} is served by the ${schoolDistrict} school district.`
    });
  }

  if (Array.isArray(openHouses) && openHouses.length > 0) {
    const times = openHouses
      .map((oh) => oh?.formattedDateTimeDisplay)
      .filter(Boolean)
      .join('; ');
    if (times) {
      items.push({
        question: `Are there any open houses at ${label}?`,
        answer: `Yes. Upcoming ${pluralize('open house', openHouses.length)}: ${times}.`
      });
    }
  }

  return compact(items);
};

/**
 * Builds local-intent Q&A pairs for a community page from existing Contentful fields.
 * @param {object} community - the community data object (COMMUNITY_ATTRIBUTES fields)
 * @returns {Array<{question: string, answer: string, link?: string}>}
 */
export const buildCommunityFaqItems = (community = {}) => {
  const {
    title,
    introductionText,
    summaryText,
    transportationText,
    shoppingText,
    recreationText,
    hospitalsText,
    viewActivePropertiesLink
  } = community;

  const name = title || 'this community';
  const items = [];

  const knownFor = firstParagraph(introductionText) || firstParagraph(summaryText);
  if (knownFor) {
    items.push({ question: `What is ${name} known for?`, answer: knownFor });
  }

  if (transportationText) {
    items.push({
      question: `What is transportation like in ${name}?`,
      answer: firstParagraph(transportationText)
    });
  }

  if (shoppingText) {
    items.push({
      question: `What shopping is available in ${name}?`,
      answer: firstParagraph(shoppingText)
    });
  }

  if (recreationText) {
    items.push({
      question: `What recreation is available in ${name}?`,
      answer: firstParagraph(recreationText)
    });
  }

  if (hospitalsText) {
    items.push({
      question: `What healthcare and hospitals serve ${name}?`,
      answer: firstParagraph(hospitalsText)
    });
  }

  if (viewActivePropertiesLink) {
    items.push({
      question: `Are there homes for sale in ${name}?`,
      answer: `Yes. Browse the current active listings in ${name}.`,
      link: viewActivePropertiesLink
    });
  }

  return compact(items);
};

/**
 * Builds Q&A from community data that is NOT already covered by the section
 * accordion (nearby communities, active listings, local events, service area).
 * Intended for the visible FAQ block so it doesn't simply restate the accordion.
 * @param {object} community - the community data object
 * @param {object} [extras]
 * @param {Array} [extras.events] - upcoming local events for the community
 * @returns {Array<{question: string, answer: string, link?: string, linkLabel?: string}>}
 */
export const buildCommunityHighlightFaqItems = (community = {}, { events = [] } = {}) => {
  const { title, slug, serviceArea, viewActivePropertiesLink, nearbyCommunities } = community;
  const name = title || 'this community';
  const items = [];

  if (viewActivePropertiesLink) {
    items.push({
      question: `Are there homes for sale in ${name}?`,
      answer: `Yes. Browse the current active listings in ${name}.`,
      link: viewActivePropertiesLink
    });
  }

  const nearby = (nearbyCommunities?.items || [])
    .map((c) => c?.title || c?.builderDevelopmentName)
    .filter(Boolean)
    .slice(0, 5);
  if (nearby.length > 0) {
    items.push({
      question: `What communities are near ${name}?`,
      answer: `Communities near ${name} include ${joinWithAnd(nearby)}.`
    });
  }

  if (Array.isArray(events) && events.length > 0) {
    items.push({
      question: `Are there local events in ${name}?`,
      answer: `Yes. ${name} has ${plural(events.length, 'upcoming local event')} listed.`,
      ...(slug
        ? { link: `/community/${slug}?tab=local-events`, linkLabel: 'View local events' }
        : {})
    });
  }

  if (serviceArea) {
    items.push({
      question: `What area is ${name} part of?`,
      answer: `${name} is part of the ${serviceArea}.`
    });
  }

  return compact(items);
};

/**
 * Builds localized Q&A pairs for an office detail page from structured office data.
 * @param {object} params
 * @param {object} params.officeData - office API data (name, address, phone, agents…)
 * @param {object} [params.cmsData] - Contentful office data (title, slug, nearby communities)
 * @param {Array} [params.managers] - manager agent records ({ agent: { name } })
 * @param {Array} [params.agents] - non-manager agent records
 * @returns {Array<{question: string, answer: string, link?: string, linkLabel?: string}>}
 */
export const buildOfficeFaqItems = ({
  officeData = {},
  cmsData = {},
  managers = [],
  agents = []
}) => {
  const { name, address, city, state, zipCode, phone, fax } = officeData;
  const officeName = name || cmsData.title || 'this Sibcy Cline';
  const cityState = [city, state].filter(Boolean).join(', ');

  const items = [];

  if (address && cityState) {
    items.push({
      question: `Where is the ${officeName} office located?`,
      answer: `The ${officeName} office is located at ${address}, ${cityState}${
        zipCode ? ` ${zipCode}` : ''
      }.`
    });
  }

  const managerNames = managers
    .map((m) => m?.agent?.name)
    .filter(Boolean)
    .join(' and ');
  if (managerNames) {
    items.push({
      question: `Who manages the ${officeName} office and how do I reach them?`,
      answer: `The ${officeName} office is managed by ${managerNames}.${
        phone ? ` Call ${phone} to connect with the office.` : ''
      }`
    });
  } else if (phone) {
    items.push({
      question: `How do I contact the ${officeName} office?`,
      answer: `You can reach the ${officeName} office by phone at ${phone}${
        fax ? ` or by fax at ${fax}` : ''
      }.`
    });
  }

  const agentTotal = (managers?.length || 0) + (agents?.length || 0);
  if (agentTotal > 0) {
    items.push({
      question: `Can I meet with an agent at the ${officeName} office?`,
      answer: `Yes. The ${officeName} office is home to ${plural(
        agentTotal,
        'real estate agent'
      )}. Stop by${address ? ` at ${address}` : ''} or contact the office to be matched with an agent.`
    });
  }

  const serviceCommunities = (cmsData.nearbyCommunitiesCollection?.items || [])
    .map((c) => c?.title)
    .filter(Boolean);
  if (serviceCommunities.length > 0) {
    items.push({
      question: `What areas does the ${officeName} office serve?`,
      answer: `The ${officeName} office serves ${serviceCommunities.join(
        ', '
      )} and the surrounding areas.`
    });
  }

  // Everything above is generated from real, office-specific data. The two entries
  // below are boilerplate — identical for every office — so only add them when the
  // office produced at least one data-driven Q&A. That keeps a data-less office from
  // emitting a page of purely templated FAQ (an AEO-spam smell).
  if (items.length === 0) return [];

  // Company-level services offered through every office (factual, not per-office data).
  items.push({
    question: `Can the ${officeName} office help with buying, selling, mortgage, and relocation?`,
    answer: `Yes. Through the ${officeName} office, Sibcy Cline offers home buying and selling, mortgage, insurance, title, and relocation services.`
  });

  if (cmsData.slug) {
    items.push({
      question: `Are there homes for sale listed by the ${officeName} office?`,
      answer: `Yes. Browse the newest listings from the ${officeName} office.`,
      link: `/office/${cmsData.slug}/listings`,
      linkLabel: 'View office listings'
    });
  }

  return compact(items);
};

/**
 * Builds Q&A pairs for a builder or development page from plain (non-rich-text)
 * fields. Rich-text sections (features, floorplans, schools) are intentionally left
 * to CMS-authored FAQ content rather than extracted here.
 * @param {object} data - builder/development page data
 * @returns {Array<{question: string, answer: string, link?: string, linkLabel?: string}>}
 */
export const buildBuilderDevelopmentFaqItems = (data = {}) => {
  const { builderDevelopmentName, category, excerpt, serviceArea, slug } = data;

  const name = builderDevelopmentName || 'this community';
  const isBuilder = category === 'Builder';
  const basePath = isBuilder ? 'builder' : 'development';

  const items = [];

  const summary = firstParagraph(excerpt);
  if (summary) {
    items.push({ question: `What is ${name}?`, answer: summary });
  }

  if (serviceArea) {
    items.push({
      question: isBuilder ? `Where does ${name} build?` : `Where is ${name} located?`,
      answer: isBuilder
        ? `${name} builds in the ${serviceArea} area.`
        : `${name} is located in the ${serviceArea} area.`
    });
  }

  const availabilityAnswer = isBuilder
    ? `Yes. View homes and communities available from ${name} on the Properties tab.`
    : `Yes. View the homes currently available in ${name} on the Properties tab.`;
  items.push({
    question: `Are there homes available in ${name}?`,
    answer: availabilityAnswer,
    ...(slug
      ? { link: `/${basePath}/${slug}?tab=properties`, linkLabel: 'View available homes' }
      : {})
  });

  return compact(items);
};

const KNOWN_STATUSES = new Set(LISTING_STATUSES.map((s) => s.toLowerCase()));
const CURRENT_STATUSES = new Set(CURRENT_LISTING_STATUSES.map((s) => s.toLowerCase()));

/**
 * Builds a qualifier for the market-summary copy (e.g. "sold") so non-current
 * inventory is never described as listings available now. Returns null for
 * current-inventory searches, which need no qualifier. The `status` param is
 * user-controlled and the copy is crawlable SSR text, so values outside the known
 * vocabulary are dropped rather than echoed.
 *
 * @param {string|string[]} status - the `status` query param
 * @returns {string|null}
 */
export const describeListingStatuses = (status) => {
  const selected = [
    ...new Set(
      toArray(status)
        .filter((value) => typeof value === 'string')
        .map((value) => value.trim().toLowerCase())
        .filter((value) => KNOWN_STATUSES.has(value))
    )
  ];

  if (selected.length === 0) return null;
  if (selected.every((value) => CURRENT_STATUSES.has(value))) return null;

  return joinWithAnd(selected);
};

// Median of an already-sorted numeric array.
const medianOfSorted = (sorted) => {
  if (!sorted.length) return null;
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

/**
 * Computes a market summary from a slim (price + status) result set.
 *
 * `totalCount` must come from the SAME fetch as `items`, so the sampled flag is
 * decided against matching numbers — a set of exactly `cap` items is complete when
 * that fetch's total says so. The sample median is always kept;
 * applyMarketSummaryTotal decides whether it is shown.
 *
 * @param {Array<{price: number, status: string}>} items
 * @param {number|null} totalCount - total from the same query that produced items
 * @param {number} cap - max items fetched for the summary
 * @returns {object|null}
 */
export const computeMarketSummary = (items, totalCount, cap) => {
  if (!items || items.length === 0) return null;

  const isSampled =
    typeof totalCount === 'number' ? totalCount > items.length : items.length >= cap;

  const has = (status, needle) =>
    String(status || '')
      .toLowerCase()
      .includes(needle);
  const activeCount = items.filter((i) => has(i.status, 'active')).length;
  const pendingCount = items.filter((i) => has(i.status, 'pending')).length;

  const prices = items
    .map((i) => i.price)
    .filter((p) => typeof p === 'number' && p > 0)
    .sort((a, b) => a - b);

  return {
    totalListings: typeof totalCount === 'number' ? totalCount : null,
    activeCount,
    pendingCount,
    minPrice: prices.length ? prices[0] : null,
    maxPrice: prices.length ? prices[prices.length - 1] : null,
    medianPrice: medianOfSorted(prices),
    isSampled
  };
};

/**
 * Applies a freshly-read totalCount to a (possibly cached) market summary, so the
 * headline agrees with the live "N properties" count.
 *
 * The sampled flag is OR-ed, not replaced: an aggregate built from a partial sample
 * stays sampled even once the total drops below the cap, otherwise its min/max
 * would read as an exact range.
 *
 * @param {object|null} summary - result of computeMarketSummary (may be cached)
 * @param {number} totalCount - exact total from the current request
 * @param {number} cap - max items fetched for the summary
 * @returns {object|null}
 */
export const applyMarketSummaryTotal = (summary, totalCount, cap) => {
  if (!summary) return null;

  const isSampled = Boolean(summary.isSampled) || totalCount > cap;

  return {
    ...summary,
    totalListings: totalCount,
    isSampled,
    medianPrice: isSampled ? null : summary.medianPrice
  };
};
